import { useReactFlow } from "@xyflow/react";

import { defaultWebhookTriggerNode } from "../Models/WebhookTrigger";
import { defaultAssistantNode } from "../Models/Assistant";
import { defaultConditionalNode } from "../Models/Conditional";
import { NodeType } from "../Models/NodeType";
import { TCatalogNode } from "../Models/nodeCatalog";
import { nanoid } from "nanoid";
import { defaultExternalApiNode } from "../Models/ExternalApi";
import { defaultEndNode } from "../Models/End";
import { defaultWaitForCallbackNode } from "../Models/WaitForCallback";
import { defaultDelayNode } from "../Models/Delay";

export default useNodeAdd;

function useNodeAdd(sourceNode, selectedBranch, onAddNode) {
  const { setNodes, addNodes, addEdges, setEdges, getEdges } = useReactFlow();

  const addNode = (targetCatalogNode: TCatalogNode) => {
    const { type, subType } = targetCatalogNode;

    const defaultNodeData = {
      [NodeType.WEBHOOK_TRIGGER]: defaultWebhookTriggerNode,
      [NodeType.ASSISTANT]: defaultAssistantNode[subType],
      [NodeType.CONDITIONAL]: defaultConditionalNode[subType],
      [NodeType.EXTERNAL_API]: defaultExternalApiNode,
      [NodeType.END]: defaultEndNode[subType],
      [NodeType.WAIT_FOR_CALLBACK]: defaultWaitForCallbackNode,
      [NodeType.DELAY]: defaultDelayNode,
    };

    const data = defaultNodeData[type];
    if (!data) return;

    const newNode = {
      id: nanoid(),
      data: { ...data },
      type,
      position: { x: 0, y: 0 },
    };

    if (type === NodeType.WEBHOOK_TRIGGER) {
      setNodes([newNode]);
      onAddNode(newNode);
      return;
    }

    const sourcePort = selectedBranch?.outputPort ?? "default";
    const existingEdge = getEdges().find(
      (edge) =>
        edge.source === sourceNode.id &&
        (edge.data?.sourcePort ?? "default") === sourcePort,
    );

    const sourceNodeToNewNodeEdge = {
      id: `${sourceNode.id}->${newNode.id}->${sourcePort}`,
      source: sourceNode.id,
      sourceHandle: sourcePort,
      label: sourcePort,
      target: newNode.id,
      type: "default",
      data: { sourcePort },
      deletable: false,
    };

    addNodes(newNode);

    if (existingEdge) {
      const newNodeToTargetNodeEdge = {
        id: `${newNode.id}->${existingEdge.target}->default`,
        source: newNode.id,
        target: existingEdge.target,
        type: "default",
        sourceHandle: "default",
        label: "default",
        data: { sourcePort: "default" },
        deletable: false,
      };

      setEdges((edges) => [
        ...edges.filter((e) => e.id !== existingEdge.id),
        sourceNodeToNewNodeEdge,
        newNodeToTargetNodeEdge,
      ]);
    } else {
      addEdges(sourceNodeToNewNodeEdge);
    }
    onAddNode(newNode);
  };

  return { addNode };
}
